import { env } from '../env.config';

// ========== CONFIGURATION API CENTRALISÉE - PRIORITÉ HAUTE ==========

/**
 * Configuration centralisée pour tous les endpoints API
 * Remplace les URLs dispersées dans les services individuels
 */
export const API_CONFIG = {
  // Base URL de l'API
  BASE_URL: env.API_BASE_URL,

  // Timeout par défaut pour les requêtes
  DEFAULT_TIMEOUT: 10000,

  // Retry configuration
  RETRY_CONFIG: {
    attempts: 3,
    delay: 1000,
    backoffMultiplier: 2,
    maxDelay: 30000,
  },

  // Endpoints par catégorie
  ENDPOINTS: {
    // Authentification
    AUTH: {
      BASE: '/api/auth',
      LOGIN: '/api/auth/login',
      REGISTER: '/api/auth/register',
      REGISTER_DOCTOR: '/api/auth/register-doctor',
      LOGOUT: '/api/auth/logout',
      VERIFY: '/api/auth/me', // aligné backend
      ME: '/api/auth/me',
      REFRESH: '/api/auth/refresh',
      CHECK_EMAIL: '/api/auth/check-email',
      MFA: {
        STATUS: (userId: number) => `/api/auth/mfa/status/${userId}`,
        SETUP: '/api/auth/mfa/setup',
        ENABLE: '/api/auth/mfa/enable',
        VERIFY: '/api/auth/mfa/verify',
        DISABLE: '/api/auth/mfa/disable',
        REGENERATE_BACKUP_CODES: '/api/auth/mfa/regenerate-backup-codes',
        LOGIN_VERIFY: '/api/auth/login/verify',
      },
      SSO: {
        GOOGLE: '/api/auth/sso/google',
        FACEBOOK: '/api/auth/sso/facebook',
      },
    },

    // Rendez-vous
    APPOINTMENTS: {
      BASE: '/api/appointments',
      BY_ID: (id: number) => `/api/appointments/${id}`,
      CONFIRM: (id: number) => `/api/appointments/${id}/confirm`,
      CANCEL: (id: number) => `/api/appointments/${id}/cancel`,
      NO_SHOW: (id: number) => `/api/appointments/${id}/no-show`,
    },

    // Consultations
    CONSULTATIONS: {
      BASE: '/api/consultations',
      BY_ID: (id: number) => `/api/consultations/${id}`,
      START: (id: number) => `/api/consultations/${id}/start`,
      COMPLETE: (id: number) => `/api/consultations/${id}/complete`,
      CANCEL: (id: number) => `/api/consultations/${id}/cancel`,
      URGENT_LIST: '/api/consultations/urgent/list',
    },

    // Téléconsultations
    TELECONSULT: {
      BASE: '/api/teleconsult',
      BY_ID: (id: number) => `/api/teleconsult/${id}`,
      URGENT: (id: number) => `/api/teleconsult/${id}/urgent`,
      URGENT_LIST: '/api/teleconsult/urgent/list',
      WAITING_ROOM: {
        BASE: '/api/teleconsult/waiting-room',
        JOIN: '/api/teleconsult/waiting-room/join',
        LEAVE: '/api/teleconsult/waiting-room/leave',
        PATIENTS: '/api/teleconsult/waiting-room/patients',
        CALL_NEXT: '/api/teleconsult/waiting-room/call-next',
        POSITION: (id: string | number) => `/api/teleconsult/waiting-room/position/${id}`,
        STATS: '/api/teleconsult/waiting-room/stats',
        CLEAR: '/api/teleconsult/waiting-room/clear',
      },
    },

    // Messagerie
    MESSAGING: {
      BASE: '/api/messaging',
      CONVERSATION: (id: number) => `/api/messaging/conversation/${id}`,
      CONVERSATIONS: '/api/messaging/conversations',
      MARK_READ: (id: number) => `/api/messaging/${id}/read`,
      UNREAD: '/api/messaging/unread',
    },

    // Appels vidéo
    VIDEO_CALLS: {
      BASE: '/api/video-calls',
      TEST: '/api/video-calls/test',
      WAITING: '/api/video-calls/waiting',
      BY_ID: (id: number) => `/api/video-calls/${id}`,
      TOKEN: (id: number) => `/api/video-calls/${id}/token`,
      START: (id: number) => `/api/video-calls/${id}/start`,
      CANCEL: (id: number) => `/api/video-calls/${id}/cancel`,
      END: (id: number) => `/api/video-calls/${id}/end`,
    },

    // Utilisateurs
    USERS: {
      BASE: '/users',
      STATS_OVERVIEW: '/users/stats/overview',
      BY_ID: (id: number) => `/users/${id}`,
      CONSULTATIONS: (id: number) => `/users/${id}/consultations`,
      SHORT_NUMBER: (id: number) => `/users/${id}/short-number`,
    },

    // Patients
    PATIENTS: {
      BASE: '/patients',
      SEARCH: '/patients/search',
      SHORT_NUMBER: (shortNumber: string) => `/patients/short-number/${shortNumber}`,
      STATS_OVERVIEW: '/patients/stats/overview',
      REALTIME_UPDATES: '/patients/realtime/updates',
      BY_ID: (id: number) => `/patients/${id}`,
      CONSULTATIONS: (id: number) => `/patients/${id}/consultations`,
      GENERATE_SHORT_NUMBER: (id: number) => `/patients/${id}/short-number`,
    },

    // Administration
    ADMIN: {
      USERS: '/admin/users',
      USER_BY_ID: (id: number) => `/admin/users/${id}`,
      STATS: '/admin/stats',
    },

    // Audit & GDPR
    AUDIT: {
      LOGS: '/audit/logs',
    },
    GDPR: {
      EXPORT: (userId: number) => `/gdpr/export/${userId}`,
      DELETE: (userId: number) => `/gdpr/delete/${userId}`,
    },
  },

  // Headers par défaut
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },

  // Configuration WebSocket
  WEBSOCKET: {
    URL: env.API_BASE_URL,
    EVENTS: {
      // Messages
      MESSAGE_NEW: 'message:new',
      MESSAGE_READ: 'message:read',

      // Appels vidéo
      VIDEO_CALL_STATUS_CHANGED: 'video_call_status_changed',

      // Statut utilisateur
      USER_ONLINE: 'user:online',
      USER_OFFLINE: 'user:offline',

      // Frappe
      TYPING: 'typing',

      // Urgences
      EMERGENCY_ALERT: 'emergency:alert',

      // Salle d'attente
      WAITING_ROOM_UPDATE: 'waiting_queue_updated',
      WAITING_ROOM_JOIN: 'teleconsultation:join_waiting_room',
      WAITING_ROOM_LEAVE: 'teleconsultation:leave_waiting_room',
      WAITING_ROOM_POSITION_UPDATE: 'queue_updated',
      WAITING_ROOM_CALL_NEXT: 'waiting_room:call_next',
      WAITING_ROOM_PRIORITY_UPDATE: 'waiting_room:priority_update',

      // Connexion
      AUTHENTICATE: 'authenticate',
      JOIN_CONVERSATION: 'join_conversation',
      LEAVE_CONVERSATION: 'leave_conversation',
      RECONNECT: 'reconnect',
    },
  },

  // Configuration cache
  CACHE: {
    DEFAULT_TTL: 5 * 60 * 1000, // 5 minutes
    STALE_TIME: 2 * 60 * 1000, // 2 minutes
    CACHE_TIME: 10 * 60 * 1000, // 10 minutes
  },
} as const;

/**
 * Utilitaires pour construire les URLs
 */
export const ApiUtils = {
  /**
   * Construit une URL complète avec la base URL
   */
  buildUrl: (endpoint: string): string => {
    return `${API_CONFIG.BASE_URL}${endpoint}`;
  },

  /**
   * Construit les headers avec authentification
   */
  buildHeaders: (token?: string): Record<string, string> => {
    const headers: Record<string, string> = { ...API_CONFIG.DEFAULT_HEADERS };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  },

  /**
   * Calcule le délai pour le retry avec backoff exponentiel
   */
  calculateRetryDelay: (attempt: number): number => {
    const { delay, backoffMultiplier, maxDelay } = API_CONFIG.RETRY_CONFIG;
    const calculatedDelay = delay * Math.pow(backoffMultiplier, attempt - 1);
    return Math.min(calculatedDelay, maxDelay);
  },
};

export default API_CONFIG;
